import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatTableModule, MatTableDataSource } from "@angular/material/table";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { ToastService } from "../../../../services/toast.service";
import { AuthService } from "../../../../auth/auth.service";
import { ToastType } from "../../../../enums/config.enum";

@Component({
  selector: 'app-surplus-lines-license-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatTableModule],
  templateUrl: './surplus-lines-license-details.component.html',
  styleUrl: './surplus-lines-license-details.component.scss'
})
export class SurplusLinesLicenseDetailsComponent implements OnInit, OnChanges {
  @Input() companyDetailForm!: FormGroup;
  @Input() companyId: string | null = null;
  @Input() isCreateCompany: boolean = true;

  // Persisted list of config records (configId/slaNumber/etc.).
  // During create-flow the tab content is destroyed/recreated; we keep this list in the parent form
  // under `producerSlaConfigList` so it survives navigation.
  private njtnList: any[] = [];
  // Explicit data source for MatTable to ensure immediate UI updates on add/remove
  producerSlaDataSource = new MatTableDataSource<FormControl>();

  constructor(
    private systemAdminService: SystemAdminService,
    private toastService: ToastService,
    private authService: AuthService
  ) {}

  // Method to get njtnList for updating after company creation
  getNjtnList(): any[] {
    return this.readPersistedConfigList();
  }

  ngOnInit(): void {
    // Re-hydrate from parent form (handles tab navigation destroying this component)
    this.njtnList = this.readPersistedConfigList();
    this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
    this.tryLoadCompanyNJTN();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] || changes['isCreateCompany']) {
      // Inputs can be set/changed after init; attempt load when needed
      this.tryLoadCompanyNJTN();
    }
  }

  get producerSlaNumbers(): FormArray {
    return (this.companyDetailForm.get('producerSlaNumbers') as FormArray) ?? new FormArray([]);
  }

  displayedColumns: string[] = ['#', 'slaNumber', 'action'];

  private tryLoadCompanyNJTN(): void {
    // Edit company mode only
    if (!this.companyId || this.isCreateCompany) {
      return;
    }
    // If grid already has values, keep existing local state across tab navigation
    // and avoid overwriting with stale server data on every re-entry.
    if (this.producerSlaNumbers.length > 0) {
      this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
      return;
    }
    this.loadCompanyNJTN();
  }

  private get configListCtrl(): any {
    return this.companyDetailForm?.get('producerSlaConfigList') as any;
  }

  private readPersistedConfigList(): any[] {
    const ctrl = this.configListCtrl;
    const val = ctrl?.value;
    return Array.isArray(val) ? [...val] : [];
  }

  private writePersistedConfigList(list: any[]): void {
    const ctrl = this.configListCtrl;
    if (!ctrl) {
      this.njtnList = Array.isArray(list) ? [...list] : [];
      return;
    }
    ctrl.setValue(Array.isArray(list) ? [...list] : [], { emitEvent: false });
    ctrl.markAsDirty();
    ctrl.updateValueAndValidity({ emitEvent: false });
    this.njtnList = Array.isArray(list) ? [...list] : [];
  }

  private loadCompanyNJTN(): void {
    const id = Number(this.companyId);
    if (!id) return;

    this.systemAdminService.GetNJTNForCompany(id).subscribe({
      next: (response: any) => {
        const list = Array.isArray(response) ? response : [];
        this.writePersistedConfigList(list);

        const formArray = this.producerSlaNumbers;
        formArray.clear();
        list.forEach((item: any) => {
          if (item && item.slaNumber && !item.isDeleted) {
            formArray.push(new FormControl(item.slaNumber));
          }
        });

        // Sync table datasource after initial load
        this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
      },
      error: () => {
        
      }
    });
  }

  addProducerSlaNumber(): void {
    const slaCtrl = this.companyDetailForm.get('slaNumber');
    if (!slaCtrl) {
      return;
    }

    slaCtrl.markAsTouched();
    slaCtrl.updateValueAndValidity();

    const value = (slaCtrl.value ?? '').toString().trim();
    if (!value || slaCtrl.invalid) {
      return;
    }

    // Prepare payload with new format
    const payload = {
      companySLConfig: [{
        slaNumber: value,
        createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId),
        createdDate: new Date().toISOString()
      }]
    };

    // If company not yet created, call API to get configId, then store locally
    if (!this.companyId || this.isCreateCompany) {
      this.systemAdminService.addProducerSlaNumber(payload).subscribe({
        next: (response: any) => {
          const data = response?.data;
          // Store the response data with configId for later update
          let storedData: any = null;
          if (data && Array.isArray(data) && data.length > 0) {
            storedData = { ...data[0], slaNumber: value }; // Store with slaNumber
          } else if (data && data.configId) {
            storedData = { ...data, slaNumber: value }; // Ensure slaNumber is stored
          } else {
            // If response doesn't have expected structure, store minimal data
            storedData = {
              slaNumber: value,
              createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId),
              createdDate: new Date().toISOString()
            };
          }
          const nextList = this.readPersistedConfigList();
          nextList.push(storedData);
          this.writePersistedConfigList(nextList);

          this.producerSlaNumbers.push(new FormControl(value));
          // sync table datasource
          this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
          // clear input after successful add
          slaCtrl.reset();
          slaCtrl.markAsPristine();
          slaCtrl.markAsUntouched();
          this.toastService.addNotification({
            message: response?.message || 'SLA Number added successfully.',
            type: ToastType.Success
          });
        },
        error: () => {
          this.toastService.addNotification({
            message: 'System error. Failed to add SLA Number. Please exit and contact help@AbacusAU.com.',
            type: ToastType.Error
          });
        }
      });
      return;
    }

    // If company exists, call API normally
    this.systemAdminService.addProducerSlaNumber(payload).subscribe({
      next: (response: any) => {
        const data = response?.data;
        this.producerSlaNumbers.push(new FormControl(value));
        // sync table datasource
        this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
        // clear input after successful add
        slaCtrl.reset();
        slaCtrl.markAsPristine();
        slaCtrl.markAsUntouched();
        if (data) {
          if (Array.isArray(data) && data.length > 0) {
            const nextList = this.readPersistedConfigList();
            nextList.push(data[0]);
            this.writePersistedConfigList(nextList);
          } else {
            const nextList = this.readPersistedConfigList();
            nextList.push(data);
            this.writePersistedConfigList(nextList);
          }
        }
        this.toastService.addNotification({
          message: response?.message || 'SLA Number added successfully.',
          type: ToastType.Success
        });
      },
      error: () => {
        this.toastService.addNotification({
          message: 'System error. Failed to add SLA Number. Please exit and contact help@AbacusAU.com.',
          type: ToastType.Error
        });
      }
    });
  }

  removeProducerSlaNumber(index: number): void {
    if (index < 0 || index >= this.producerSlaNumbers.length) {
      return;
    }

    const currentList = this.readPersistedConfigList();
    const existing = currentList[index];

    // If not yet persisted, just remove from the form/grid
    if (!this.companyId || this.isCreateCompany || !existing?.configId) {
      this.producerSlaNumbers.removeAt(index);
      // sync table datasource
      this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
      if (existing) {
        currentList.splice(index, 1);
        this.writePersistedConfigList(currentList);
      }
      return;
    }

    const now = new Date().toISOString();
    const payload = {
      companySLConfig: [{
        configId: existing.configId,
        companyId: existing.companyId,
        slaNumber: existing.slaNumber,
        isActive: false,
        isDeleted: true,
        updatedUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId),
        updatedDate: now
      }]
    };

    this.systemAdminService.UpdateNJTN(payload).subscribe({
      next: (response: any) => {
        this.producerSlaNumbers.removeAt(index);
        // sync table datasource
        this.producerSlaDataSource.data = this.producerSlaNumbers.controls as FormControl[];
        const nextList = this.readPersistedConfigList();
        nextList.splice(index, 1);
        this.writePersistedConfigList(nextList);
        this.toastService.addNotification({
          message: response?.message || 'SLA Number removed successfully.',
          type: ToastType.Success
        });
      },
      error: () => {
        this.toastService.addNotification({
          message: 'System error. Failed to remove SLA Number. Please exit and contact help@AbacusAU.com.',
          type: ToastType.Error
        });
      }
    });
  }
}